// src/components/SystemSettingsAccordion.jsx
//
// Self-contained accordion for SuperAdminPanel. Manages /config/systemSettings:
//   - currentSchoolYear: string (e.g. '2025-2026')
//   - archivedSchoolYears: string[]
//
// Also exposes the "Archive current year" action which calls the
// archiveCheckoutYear Cloud Function: marks all current-year checkout
// records as archived and rolls the current year forward.
//
// On first load, if /config/systemSettings doesn't exist, seeds it with
// DEFAULT_SCHOOL_YEAR ('2025-2026'). Per Kyle's preferences, this is a
// soft seed — the superadmin should verify the value.

import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  DEFAULT_SCHOOL_YEAR,
  nextSchoolYear,
  KA_COLORS,
} from '../utils/checkoutHelpers';

const styles = {
  accordion: {
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
    background: '#fff',
    marginBottom: 16,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: (open) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    cursor: 'pointer',
    background: open ? KA_COLORS.lightGray : '#fff',
    borderRadius: open ? '6px 6px 0 0' : 6,
    borderBottom: open ? `1px solid ${KA_COLORS.borderGray}` : 'none',
    userSelect: 'none',
  }),
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  icon: { fontSize: 18 },
  title: {
    fontSize: 15,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  subtitle: { fontSize: 12, color: '#666', margin: '2px 0 0' },
  chevron: (open) => ({
    fontSize: 14,
    color: '#666',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
    transition: 'transform 0.15s',
  }),
  body: { padding: 18 },
  fieldRow: { marginBottom: 16 },
  fieldLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
  },
  fieldHint: {
    fontSize: 11,
    color: '#777',
    marginTop: 4,
    fontStyle: 'italic',
  },
  inputRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  input: {
    padding: '7px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 14,
    width: 140,
  },
  saveBtn: (disabled) => ({
    padding: '7px 14px',
    border: 'none',
    background: disabled ? '#ccc' : KA_COLORS.red,
    color: '#fff',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
  }),
  archiveSection: {
    marginTop: 20,
    padding: 14,
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: 6,
  },
  archiveTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#856404',
    margin: '0 0 6px',
  },
  archiveDesc: {
    fontSize: 12,
    color: '#5D4037',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  archiveBtn: (disabled) => ({
    padding: '7px 14px',
    border: 'none',
    background: disabled ? '#ccc' : '#856404',
    color: '#fff',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
  }),
  archivedList: {
    marginTop: 10,
    fontSize: 12,
    color: '#666',
  },
  archivedPill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    background: KA_COLORS.lightGray,
    border: `1px solid ${KA_COLORS.borderGray}`,
    margin: '0 4px 4px 0',
    fontSize: 11,
    color: '#555',
  },
  successBox: {
    marginTop: 10,
    padding: 8,
    background: '#D4EDDA',
    border: '1px solid #C3E6CB',
    borderRadius: 4,
    color: '#155724',
    fontSize: 12,
  },
  errorBox: {
    marginTop: 10,
    padding: 8,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 4,
    color: '#C62828',
    fontSize: 12,
  },
  loadingNote: {
    padding: 12,
    fontSize: 12,
    color: '#666',
    fontStyle: 'italic',
  },
  bulkSection: {
    marginTop: 20,
    padding: 14,
    background: 'rgba(26,54,93,0.04)',
    border: '1px solid rgba(26,54,93,0.2)',
    borderRadius: 6,
  },
  bulkTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: '0 0 6px',
  },
  bulkDesc: {
    fontSize: 12,
    color: '#444',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  bulkBtnRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  bulkBtn: (disabled) => ({
    padding: '7px 14px',
    border: 'none',
    background: disabled ? '#ccc' : KA_COLORS.navy,
    color: '#fff',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
  }),
  sisListInput: {
    width: '100%',
    minHeight: 70,
    padding: 8,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'monospace',
    boxSizing: 'border-box',
    resize: 'vertical',
    margin: '8px 0',
  },
  bulkResultBox: {
    marginTop: 10,
    padding: 10,
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 12,
    color: '#333',
    maxHeight: 240,
    overflowY: 'auto',
  },
  resetSection: {
    marginTop: 20,
    padding: 14,
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 6,
  },
  resetTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#C62828',
    margin: '0 0 6px',
  },
  resetDesc: {
    fontSize: 12,
    color: '#7f1d1d',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  resetBtn: (disabled) => ({
    padding: '7px 14px',
    border: 'none',
    background: disabled ? '#ccc' : '#C62828',
    color: '#fff',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 700,
  }),
};

// -----------------------------------------------------------------------------

const SystemSettingsAccordion = ({ user, defaultOpen = false }) => {
  // Pull isSuperAdmin straight from the auth context. It lives at the top
  // level of the context value, NOT on the user object — so the older
  // `user?.isSuperAdmin` gate would always be falsy and hide this whole
  // accordion. Keep the `user` prop as-is for any callers that pass it,
  // but rely on the hook for the gate.
  const auth = useAuth();
  const isSuperAdmin = auth?.isSuperAdmin || user?.isSuperAdmin || false;

  const [open, setOpen] = useState(defaultOpen);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [yearInput, setYearInput] = useState('');
  const [statusMsg, setStatusMsg] = useState(null); // { type: 'success'|'error', text }

  // Bulk-create state
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [sisListInput, setSisListInput] = useState('');

  // Reset state
  const [resetRunning, setResetRunning] = useState(false);

  // Subscribe to settings doc
  useEffect(() => {
    const ref = doc(db, 'config', 'systemSettings');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSettings({
            currentSchoolYear: DEFAULT_SCHOOL_YEAR,
            archivedSchoolYears: [],
            __seeded: false,
          });
        } else {
          setSettings(snap.data());
        }
        setLoading(false);
      },
      (err) => {
        console.error('systemSettings load failed:', err);
        setStatusMsg({ type: 'error', text: 'Could not load system settings.' });
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // Sync year input when settings load
  useEffect(() => {
    if (settings?.currentSchoolYear && !yearInput) {
      setYearInput(settings.currentSchoolYear);
    }
  }, [settings, yearInput]);

  const isYearValid = /^\d{4}-\d{4}$/.test(yearInput.trim());
  const isYearChanged =
    settings && yearInput.trim() !== settings.currentSchoolYear;

  const handleSaveYear = async () => {
    if (!isYearValid) {
      setStatusMsg({
        type: 'error',
        text: 'Year must be in format YYYY-YYYY (e.g. 2025-2026).',
      });
      return;
    }
    setSaving(true);
    setStatusMsg(null);
    try {
      const ref = doc(db, 'config', 'systemSettings');
      await setDoc(
        ref,
        {
          currentSchoolYear: yearInput.trim(),
          archivedSchoolYears: settings?.archivedSchoolYears || [],
          updatedBy: user?.uid || null,
          updatedByName: user?.displayName || user?.email || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setStatusMsg({
        type: 'success',
        text: `Current school year set to ${yearInput.trim()}.`,
      });
    } catch (err) {
      console.error('Save year failed:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'Failed to save.',
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Toggle the studentLoginEnabled flag on /config/systemSettings. When
   * false (default), students who log in are blocked with the same message
   * faculty see when their email matches /students. When true, those
   * students proceed to a constrained read-only view of their own
   * checkout record.
   */
  const handleToggleStudentLogin = async (newValue) => {
    setSaving(true);
    setStatusMsg(null);
    try {
      const ref = doc(db, 'config', 'systemSettings');
      await setDoc(
        ref,
        {
          studentLoginEnabled: !!newValue,
          updatedBy: user?.uid || null,
          updatedByName: user?.displayName || user?.email || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setStatusMsg({
        type: 'success',
        text: newValue
          ? 'Student logins are now ENABLED. Students with a matching /students record can sign in to view their checkout.'
          : 'Student logins are now DISABLED. Students attempting to sign in will be blocked.',
      });
    } catch (err) {
      console.error('Toggle student login failed:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'Failed to update.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveYear = async () => {
    if (!settings?.currentSchoolYear) return;
    const next = nextSchoolYear(settings.currentSchoolYear);
    if (!next) {
      setStatusMsg({
        type: 'error',
        text: 'Could not compute next school year. Check the format.',
      });
      return;
    }

    const confirmed = window.confirm(
      `This will archive ALL checkout records for ${settings.currentSchoolYear} ` +
        `and roll the current year forward to ${next}.\n\n` +
        'Archived records will become read-only but remain accessible from the ' +
        'Archive tab. New checkout records will be created under the new year.\n\n' +
        'Continue?',
    );
    if (!confirmed) return;

    setArchiving(true);
    setStatusMsg(null);
    try {
      const functions = getFunctions();
      const archiveCheckoutYear = httpsCallable(functions, 'archiveCheckoutYear');
      const result = await archiveCheckoutYear({
        yearToArchive: settings.currentSchoolYear,
        newCurrentYear: next,
      });
      const archivedCount = result?.data?.archivedCount ?? 0;
      setStatusMsg({
        type: 'success',
        text: `Archived ${archivedCount} record${archivedCount === 1 ? '' : 's'} from ${settings.currentSchoolYear}. Current year is now ${next}.`,
      });
    } catch (err) {
      console.error('Archive year failed:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'Failed to archive year.',
      });
    } finally {
      setArchiving(false);
    }
  };

  // Bulk-create checkout records.
  // mode: 'all-grade-12' | 'from-sis-list'
  const handleBulkCreate = async (mode) => {
    setBulkRunning(true);
    setStatusMsg(null);
    setBulkResult(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'bulkCreateCheckoutRecords');
      const payload = { mode };
      if (mode === 'from-sis-list') {
        const sisIds = sisListInput
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (sisIds.length === 0) {
          setStatusMsg({
            type: 'error',
            text: 'Please enter at least one SIS ID.',
          });
          setBulkRunning(false);
          return;
        }
        payload.sisIds = sisIds;
      }

      let confirmMsg;
      if (mode === 'all-grade-12') {
        confirmMsg = 'This will create checkout records for every Grade 12 student who ' +
          'doesn\'t already have one. Continue?';
      } else if (mode === 'all-upper-school') {
        confirmMsg = 'This will create checkout records for every Grade 9–12 student ' +
          'who doesn\'t already have one. (Existing records, including any ' +
          'previously created for seniors, are preserved.) Continue?';
      } else {
        confirmMsg = 'This will create checkout records for the SIS IDs you entered. ' +
          'Continue?';
      }
      if (!window.confirm(confirmMsg)) {
        setBulkRunning(false);
        return;
      }

      const result = await fn(payload);
      setBulkResult(result?.data || null);
    } catch (err) {
      console.error('bulkCreateCheckoutRecords failed:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'Bulk-create failed.',
      });
    } finally {
      setBulkRunning(false);
    }
  };

  // Wipe all checkout records and rosters. Hard reset for clean re-test.
  const handleResetData = async () => {
    const confirm1 = window.prompt(
      'This will DELETE ALL checkout records and rosters. Type "DELETE" to ' +
        'confirm.',
    );
    if (confirm1 !== 'DELETE') {
      setStatusMsg({ type: 'error', text: 'Reset cancelled (text did not match).' });
      return;
    }
    setResetRunning(true);
    setStatusMsg(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'resetCheckoutData');
      const result = await fn({ confirm: 'YES_DELETE_ALL' });
      const data = result?.data || {};
      setStatusMsg({
        type: 'success',
        text:
          `Reset complete. Deleted ${data.recordsDeleted ?? 0} record(s) ` +
          `and ${data.rostersDeleted ?? 0} roster(s). ` +
          `/config/checkoutPointPeople was preserved.`,
      });
    } catch (err) {
      console.error('resetCheckoutData failed:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'Reset failed.',
      });
    } finally {
      setResetRunning(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div style={styles.accordion}>
      <div
        style={styles.header(open)}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div style={styles.headerLeft}>
          <span style={styles.icon}>⚙️</span>
          <div>
            <h3 style={styles.title}>System Settings</h3>
            <p style={styles.subtitle}>
              School year configuration and yearly rollover
            </p>
          </div>
        </div>
        <span style={styles.chevron(open)}>▶</span>
      </div>

      {open && (
        <div style={styles.body}>
          {loading ? (
            <div style={styles.loadingNote}>Loading settings…</div>
          ) : (
            <>
              <div style={styles.fieldRow}>
                <label style={styles.fieldLabel} htmlFor="schoolYearInput">
                  Current school year
                </label>
                <div style={styles.inputRow}>
                  <input
                    id="schoolYearInput"
                    type="text"
                    style={styles.input}
                    value={yearInput}
                    onChange={(e) => setYearInput(e.target.value)}
                    placeholder="2025-2026"
                  />
                  <button
                    type="button"
                    style={styles.saveBtn(
                      saving || !isYearValid || !isYearChanged,
                    )}
                    onClick={handleSaveYear}
                    disabled={saving || !isYearValid || !isYearChanged}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <p style={styles.fieldHint}>
                  Format: YYYY-YYYY. New checkout records will be stamped with
                  this year. To roll forward to the next year, use the
                  "Archive &amp; advance" action below — don't manually edit.
                </p>
              </div>

              {settings?.archivedSchoolYears?.length > 0 && (
                <div style={styles.fieldRow}>
                  <label style={styles.fieldLabel}>Archived school years</label>
                  <div style={styles.archivedList}>
                    {settings.archivedSchoolYears.map((yr) => (
                      <span key={yr} style={styles.archivedPill}>
                        {yr}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  ...styles.archiveSection,
                  background: settings?.studentLoginEnabled ? '#E8F5E9' : '#F5F5F5',
                  borderColor: settings?.studentLoginEnabled ? '#A5D6A7' : '#E0E0E0',
                }}
              >
                <h4 style={styles.archiveTitle}>Student access</h4>
                <p style={styles.archiveDesc}>
                  When enabled, students whose email matches a record in{' '}
                  <code>/students</code> can sign in to view their own
                  checkout sheet (read-only). They cannot see other students,
                  internal notes, or any other faculty data.
                  <br />
                  <br />
                  Status:{' '}
                  <strong
                    style={{
                      color: settings?.studentLoginEnabled
                        ? '#1B5E20'
                        : '#999',
                    }}
                  >
                    {settings?.studentLoginEnabled
                      ? 'ENABLED — students can log in'
                      : 'DISABLED — students are blocked'}
                  </strong>
                </p>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button
                    type="button"
                    style={{
                      ...styles.archiveBtn(saving || settings?.studentLoginEnabled),
                      background: '#2E7D32',
                      borderColor: '#2E7D32',
                    }}
                    onClick={() => handleToggleStudentLogin(true)}
                    disabled={saving || settings?.studentLoginEnabled}
                  >
                    Enable student logins
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.archiveBtn(saving || !settings?.studentLoginEnabled),
                      background: '#fff',
                      color: '#222',
                      borderColor: '#999',
                    }}
                    onClick={() => handleToggleStudentLogin(false)}
                    disabled={saving || !settings?.studentLoginEnabled}
                  >
                    Disable student logins
                  </button>
                </div>
              </div>

              <div style={styles.archiveSection}>
                <h4 style={styles.archiveTitle}>
                  End-of-year: archive &amp; advance
                </h4>
                <p style={styles.archiveDesc}>
                  When the school year ends and all checkouts are processed,
                  archive the current year. This marks all{' '}
                  <strong>{settings?.currentSchoolYear}</strong> checkout records
                  as archived (read-only) and rolls the current year forward to{' '}
                  <strong>
                    {nextSchoolYear(settings?.currentSchoolYear) || '???'}
                  </strong>
                  . Archived records remain accessible from the Archive tab.
                </p>
                <button
                  type="button"
                  style={styles.archiveBtn(archiving || !settings?.currentSchoolYear)}
                  onClick={handleArchiveYear}
                  disabled={archiving || !settings?.currentSchoolYear}
                >
                  {archiving ? 'Archiving…' : `Archive ${settings?.currentSchoolYear} & advance`}
                </button>
              </div>

              {/* Bulk record creation */}
              <div style={styles.bulkSection}>
                <h4 style={styles.bulkTitle}>Bulk-create checkout records</h4>
                <p style={styles.bulkDesc}>
                  Create checkout records in bulk for{' '}
                  <strong>{settings?.currentSchoolYear}</strong>. Existing records
                  are preserved (no duplicates). Records inherit the current
                  rosters at creation time, so populate rosters first if you
                  can.
                </p>
                <div style={styles.bulkBtnRow}>
                  <button
                    type="button"
                    style={styles.bulkBtn(bulkRunning)}
                    onClick={() => handleBulkCreate('all-upper-school')}
                    disabled={bulkRunning}
                  >
                    {bulkRunning ? 'Creating…' : 'Create for all Upper School (Grades 9–12)'}
                  </button>
                  <button
                    type="button"
                    style={styles.bulkBtn(bulkRunning)}
                    onClick={() => handleBulkCreate('all-grade-12')}
                    disabled={bulkRunning}
                  >
                    {bulkRunning ? 'Creating…' : 'Create for Grade 12 only'}
                  </button>
                </div>

                <p style={{ ...styles.bulkDesc, marginTop: 14, marginBottom: 4 }}>
                  Or create records for a specific list of SIS IDs (paste below,
                  separated by spaces, commas, or newlines):
                </p>
                <textarea
                  style={styles.sisListInput}
                  value={sisListInput}
                  onChange={(e) => setSisListInput(e.target.value)}
                  placeholder="100123 100456 100789"
                  disabled={bulkRunning}
                />
                <div style={styles.bulkBtnRow}>
                  <button
                    type="button"
                    style={styles.bulkBtn(bulkRunning || !sisListInput.trim())}
                    onClick={() => handleBulkCreate('from-sis-list')}
                    disabled={bulkRunning || !sisListInput.trim()}
                  >
                    {bulkRunning ? 'Creating…' : 'Create from SIS list'}
                  </button>
                </div>

                {bulkResult && (
                  <div style={styles.bulkResultBox}>
                    <p style={{ margin: '0 0 6px', fontWeight: 700, color: KA_COLORS.navy }}>
                      Bulk-create result
                    </p>
                    <p style={{ margin: '2px 0' }}>
                      Mode: <code>{bulkResult.mode}</code> &nbsp;•&nbsp;
                      Year: <strong>{bulkResult.schoolYear}</strong>
                    </p>
                    <p style={{ margin: '2px 0' }}>
                      Requested: <strong>{bulkResult.requested}</strong> &nbsp;•&nbsp;
                      Created: <strong style={{ color: '#155724' }}>{bulkResult.createdCount}</strong> &nbsp;•&nbsp;
                      Already existed: <strong style={{ color: '#856404' }}>{bulkResult.skippedCount}</strong> &nbsp;•&nbsp;
                      Not found: <strong style={{ color: '#C62828' }}>{bulkResult.notFoundCount}</strong>
                    </p>
                    {bulkResult.notFound?.length > 0 && (
                      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#C62828' }}>
                        Not found: {bulkResult.notFound.slice(0, 20).join(', ')}
                        {bulkResult.notFound.length > 20 ? '…' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Hard reset */}
              <div style={styles.resetSection}>
                <h4 style={styles.resetTitle}>⚠ Reset all checkout data</h4>
                <p style={styles.resetDesc}>
                  Deletes <strong>all</strong> checkout records and rosters
                  across every school year. Permission settings in{' '}
                  <code>/config/checkoutPointPeople</code> and{' '}
                  <code>/config/systemSettings</code> are preserved. There is
                  no undo. Use only for clean test resets.
                </p>
                <button
                  type="button"
                  style={styles.resetBtn(resetRunning)}
                  onClick={handleResetData}
                  disabled={resetRunning}
                >
                  {resetRunning ? 'Resetting…' : 'Reset all checkout data'}
                </button>
              </div>

              {statusMsg && (
                <div
                  style={
                    statusMsg.type === 'success'
                      ? styles.successBox
                      : styles.errorBox
                  }
                >
                  {statusMsg.text}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemSettingsAccordion;